'use client';

/**
 * CurrencySelector - Currency switching component
 *
 * This is a CORE component that provides a currency selector dropdown for all themes.
 * Themes can style this component using the className prop and CSS variables.
 *
 * Features:
 * - Displays current selected currency with flag/symbol
 * - Dropdown with supported currencies
 * - Persists selection to localStorage
 * - Triggers lazy fetch of exchange rates on first non-default selection
 *
 * Usage:
 * ```tsx
 * // Basic dropdown
 * <CurrencySelector />
 *
 * // Compact version (symbol only)
 * <CurrencySelector variant="compact" />
 *
 * // Custom styling
 * <CurrencySelector className="my-custom-class" />
 * ```
 */

import { useCurrencyOptional } from '@/contexts/CurrencyContext';
import type { Currency } from '@/lib/config/app.config';
import {
  CURRENCY_CONFIG,
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
} from '@/lib/config/app.config';
import { useCallback, useEffect, useRef, useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface CurrencySelectorProps {
  /** Visual variant */
  variant?: 'default' | 'compact' | 'minimal';

  /** Custom CSS classes for the container */
  className?: string;

  /** Custom CSS classes for the dropdown */
  dropdownClassName?: string;

  /** Label for accessibility */
  ariaLabel?: string;

  /** Callback when currency changes (in addition to context update) */
  onChange?: (currency: Currency) => void;

  /** Show loading indicator when fetching rates */
  showLoadingState?: boolean;

  /** Force dropdown to open upward (useful when placed near bottom of viewport) */
  dropUp?: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get currency display info
 */
function getCurrencyInfo(currency: Currency) {
  const config = CURRENCY_CONFIG[currency];
  return {
    code: currency,
    symbol: config?.symbol ?? currency,
    name: config?.name ?? currency,
  };
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * CurrencySelector Component
 *
 * A dropdown for selecting the display currency.
 * Works with CurrencyContext to update the selected currency across the app.
 */
export function CurrencySelector({
  variant = 'default',
  className = '',
  dropdownClassName = '',
  ariaLabel = 'Select currency',
  onChange,
  showLoadingState = true,
  dropUp = false,
}: CurrencySelectorProps) {
  // Get currency context (optional - component works outside provider with defaults)
  const currencyContext = useCurrencyOptional();

  // Local state for when outside of provider
  const [localCurrency, setLocalCurrency] = useState<Currency>(DEFAULT_CURRENCY);

  // Dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get current currency
  const selectedCurrency = currencyContext?.selectedCurrency ?? localCurrency;
  const isLoading = currencyContext?.isLoading ?? false;

  // Handle currency selection
  const handleSelect = useCallback(
    (currency: Currency) => {
      if (currencyContext) {
        currencyContext.setSelectedCurrency(currency);
      } else {
        setLocalCurrency(currency);
      }

      onChange?.(currency);
      setIsOpen(false);
    },
    [currencyContext, onChange]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const currentInfo = getCurrencyInfo(selectedCurrency);

  // Render based on variant
  const renderTriggerContent = () => {
    switch (variant) {
      case 'compact':
        return (
          <span className="font-medium">{currentInfo.symbol}</span>
        );
      case 'minimal':
        return (
          <span className="text-sm font-medium">{currentInfo.code}</span>
        );
      case 'default':
      default:
        return (
          <>
            <span className="font-medium">{currentInfo.symbol}</span>
            <span className="ml-1 text-sm text-n3 hidden sm:inline">
              {currentInfo.code}
            </span>
          </>
        );
    }
  };

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1 px-3 py-2 rounded-lg
          border border-n6 dark:border-n3
          bg-card dark:bg-n2
          text-n1 dark:text-n8
          hover:border-n5 dark:hover:border-n4
          focus:outline-none focus:ring-2 focus:ring-primary/50
          transition-colors duration-200
          ${isLoading && showLoadingState ? 'opacity-75' : ''}
        `}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {renderTriggerContent()}

        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 text-n4 transition-transform duration-200 ${
            dropUp
              ? isOpen ? '' : 'rotate-180'
              : isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>

        {/* Loading indicator */}
        {isLoading && showLoadingState && (
          <span className="ml-1 w-3 h-3 border-2 border-n4 border-t-transparent rounded-full animate-spin" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`
            absolute z-50 min-w-[160px]
            bg-card dark:bg-n2
            border border-n6 dark:border-n3
            rounded-xl shadow-lg
            py-1
            max-h-64 overflow-y-auto
            ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}
            ${dropdownClassName}
          `}
          role="listbox"
          aria-label="Currency options"
        >
          {SUPPORTED_CURRENCIES.map((currency) => {
            const info = getCurrencyInfo(currency);
            const isSelected = currency === selectedCurrency;

            return (
              <button
                key={currency}
                type="button"
                onClick={() => handleSelect(currency)}
                className={`
                  w-full flex items-center justify-between px-4 py-2
                  text-left text-sm
                  hover:bg-n7 dark:hover:bg-n3/20
                  transition-colors duration-150
                  ${isSelected ? 'bg-primary/5 text-primary font-medium' : 'text-n1 dark:text-n8'}
                `}
                role="option"
                aria-selected={isSelected}
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium w-8">{info.symbol}</span>
                  <span>{info.code}</span>
                </span>
                <span className="text-n4 text-xs hidden sm:inline">
                  {info.name}
                </span>
                {isSelected && (
                  <svg
                    className="w-4 h-4 text-primary ml-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CurrencySelector;
